import React, { useState, useEffect, useMemo } from 'react';
import { Camera, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Calendar = ({ onDateSelect, user, selected }) => {
  const getUTCDateOnly = (date) => {
    const newDate = new Date(date);
    newDate.setUTCHours(0, 0, 0, 0);
    return newDate;
  };

  const [currentDate, setCurrentDate] = useState(() => getUTCDateOnly(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => getUTCDateOnly(selected || new Date()));
  const [daysWithPhotos, setDaysWithPhotos] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const apiKey = "HWN2M2mcbZoyFDTfpaskQV9TBDk0Ir8bu4XWDxJQiBE";
  const apiBaseUrl = process.env.REACT_APP_BACKEND_URL

  // Format date for API requests
  const formatDate = (date) => {
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };

  const months = useMemo(() => [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ], []);

  const calendarInfo = useMemo(() => {
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth();
    // Days in current UTC month
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    // Day of the week for the first day of the UTC month (0 for Sunday, 1 for Monday, etc.)
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1)).getUTCDay();
    return { daysInMonth, firstDayOfMonth };
  }, [currentDate]);

  useEffect(() => {
    // Sync with external selected date from parent
    if (selected) {
      const normalized = getUTCDateOnly(selected);
      // Only update if different to avoid loops
      if (!selectedDate || normalized.toISOString() !== selectedDate.toISOString()) {
        setSelectedDate(normalized);
      }
      // Ensure current month follows selected date
      if (!currentDate || normalized.getUTCMonth() !== currentDate.getUTCMonth() || normalized.getUTCFullYear() !== currentDate.getUTCFullYear()) {
        setCurrentDate(getUTCDateOnly(new Date(Date.UTC(normalized.getUTCFullYear(), normalized.getUTCMonth(), 1))));
      }
    }
  }, [selected]);

  useEffect(() => {
    // Initial check for required user properties
    if (!user?.feedID) {
      setError("Feed ID no disponible.");
      setIsLoading(false);
      return;
    }

    const fetchPhotoDays = async () => {
      setIsLoading(true);
      setError(null);
      setLoadingProgress(0);
      try {
        const year = currentDate.getUTCFullYear();
        const month = currentDate.getUTCMonth(); // 0-indexed

        let fetchedData;

        const username = user.feedID;
        const s3ApiBaseUrl = "https://webback.lisual.com/api/s3-images";
        const token = localStorage.getItem('authLisualToken');

        if (!token) {
          throw new Error("No se encontró el token de autenticación.");
        }

        // month + 1 because the API expects 1-indexed month
        const queryUrl = `${s3ApiBaseUrl}/${user.feedID}/monthly?year=${year}&month=${month + 1}`;
        console.log('Fetching calendar data from S3:', queryUrl);

        setLoadingProgress(30);

        const response = await fetch(queryUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('S3 Calendar API Error:', errorText);
          throw new Error(`Error al cargar datos del calendario desde S3: ${response.status}`);
        }

        fetchedData = await response.json(); // This will be List<String> (image URLs)
        console.log('S3 Calendar API Response:', fetchedData);

        setLoadingProgress(60);

        let uniqueDays = new Set();

        if (Array.isArray(fetchedData)) {
            fetchedData.forEach(item => {
                let dateString = null;
                const urlToParse = typeof item === 'string' ? item : item?.url;

                if (urlToParse) {
                    // Intentamos parsear la fecha desde el path de la URL (más fiable para la ubicación en el calendario)
                    const dateMatch = urlToParse.match(/(\d{4})\/(\d{2})\/(\d{2})/);
                    if (dateMatch) {
                        dateString = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
                    }
                }

                if (!dateString && item && item.date) {
                    dateString = item.date.slice(0, 10); // Extrae YYYY-MM-DD
                }

                if (dateString) {
                    uniqueDays.add(dateString);
                }
            });
        }

        setDaysWithPhotos(uniqueDays);
        setLoadingProgress(100);
      } catch (err) {
        console.error('Calendar fetch error:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotoDays();
  }, [currentDate, user?.feedID, user?.username, apiBaseUrl, apiKey]);

  const handleDateClick = (date) => {
    if (!date) return;

    // date is already a UTC date object from renderDays
    const newSelectedDate = getUTCDateOnly(date); // Ensure it's midnight UTC

    console.log('Date clicked (UTC midnight):', newSelectedDate.toISOString());
    console.log('Original date object from calendar cell:', date.toISOString());

    setSelectedDate(newSelectedDate);
    onDateSelect(newSelectedDate);
  };

  const renderDays = () => {
    const days = [];
    const { daysInMonth, firstDayOfMonth } = calendarInfo;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Add cells for each day of the month
    for (let i = 1; i <= daysInMonth; i++) {
      // Create date in UTC to avoid timezone issues
      const date = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), i));
      // Use full UTC date string as key
      const dayKey = date.toISOString().slice(0, 10);
      const hasPhotos = daysWithPhotos.has(dayKey);

      // Compare dates using UTC values
      const isSelected = selectedDate &&
          selectedDate.getUTCDate() === i &&
          selectedDate.getUTCMonth() === currentDate.getUTCMonth() &&
          selectedDate.getUTCFullYear() === currentDate.getUTCFullYear();

      days.push(
          <div
              key={i}
              className={
                `relative border-solid border-2 rounded-md p-2 text-center cursor-pointer
            transition-all duration-200 ease-in-out
            hover:bg-blue-100
            ${isSelected ? 'bg-blue-500 text-white' : ''}
            ${hasPhotos ? 'border-blue-300' : 'border-gray-200'}`
              }
              onClick={() => handleDateClick(date)}
          >
            {i}
            {hasPhotos && (
                <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1">
                  <Camera size={12} className="text-white" />
                </div>
            )}
          </div>
      );
    }
    return days;
  };

  const changeMonth = (offset) => {
    // Create new date in UTC to avoid timezone issues
    const newDate = new Date(Date.UTC(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth() + offset,
        1
    ));
    setCurrentDate(getUTCDateOnly(newDate)); // Ensure it's set to midnight UTC
  };

  if (error) {
    return (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col items-center justify-center p-4">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <div className="text-red-500 text-center">
              <p>{error}</p>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <button
              onClick={() => changeMonth(-1)}
              className="p-2.5 lg:p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center"
              aria-label="Mes anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h2 className="text-lg font-semibold">
            {months[currentDate.getUTCMonth()]} {currentDate.getUTCFullYear()}
          </h2>
          <button
              onClick={() => changeMonth(1)}
              className="p-2.5 lg:p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center"
              aria-label="Mes siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'].map(day => (
              <div key={day} className="text-center font-bold text-sm text-gray-600 p-2">{day}</div>
          ))}
          {isLoading ? (
              <div className="col-span-7 h-48 flex items-center justify-center">
                <div className="relative w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                      className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${loadingProgress}%` }}
                  />
                </div>
              </div>
          ) : (
              renderDays()
          )}
        </div>
      </div>
  );
};

export default Calendar;